import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import { TestIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getCenterActions } from '../../../../api/centers/get-center-actions'
import { updateCenter } from '../../../../api/centers/update-center'
import { useObserver } from '../../../../lib/contexts/observer'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../../lib/types/enums/accomodation-type/accomodation-type'
import { schoolLevelEnumMapping } from '../../../../lib/types/enums/common/school-level.enum'
import { Center } from '../../../../lib/types/models/center/center'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getOlName } from '../../../../views/ols/center/utils/functions'
import LabelWithText from '../../ols/label-with-text'
import CenterForm from '../center-form'
import { CenterAddressCoursesLabel } from '../shared/center-address-courses'
import { CenterCapsuleInformation } from '../shared/center-capsule-information'
import EditHeader from './edit-header'

type Props = {
  center: Center
}

const CenterGlobalViewInformationSection = ({ center }: Props) => {
  const nativeCountryName = useNativeCountryName()
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<'edit', Center>([
    'edit'
  ])

  const observer = useObserver({ key: 'center-update-global-view' })

  const { data: centerActions } = useQuery({
    queryKey: ['center-actions', center.id],
    queryFn: () => getCenterActions(center.id)
  })

  const updateCenterMutation = useMutation({
    mutationKey: ['update-center'],
    mutationFn: updateCenter,
    onSuccess: () => {
      toast.success('Centre mis à jour avec succès')
      observer.notify(['center-global-view'])
    },
    onError: showAxiosError
  })

  const centerAddress =
    center.accomodation === AccomodationEnum.HOST_FAMILY ? null : center.journeyPlace

  return (
    <InnerBox className="rounded-xl ">
      <div className="rounded-xl flex flex-row items-center justify-between">
        <EditHeader text="Informations" onEditClick={() => toggleModal('edit', center)} />
        <div className="flex gap-2 items-center">
          <div className="bg-primary-light p-2 rounded border border-primary-light-active flex items-center gap-1">
            <CenterCapsuleInformation
              hideTotal
              attributedPlaces={center.affectedStudentsNumber ?? 0}
              blockedPlaces={center.blockedPlacesNumber ?? 0}
              total={center.maxNumberOfPlaces ?? 0}
            />
            <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
              (Total max : {center.maxNumberOfPlaces})
            </Typography>
          </div>
          <Box className="bg-grey-light rounded p-3.5">
            <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
              Responsable secteur :{' '}
              <span className="text-grey">
                {valueRender(center.lecResponsable, (responsable) => responsable.firstName)}
              </span>
            </Typography>
          </Box>
        </div>
      </div>
      <div className="h-3" />
      <InnerBox className="bg-white-hover">
        <div className="grid grid-cols-5 gap-3">
          <LabelWithText label="Nom du centre" data={center.name} />
          <LabelWithText label="Lieu de séjour" data={valueRender(center.journeyPlace?.name)} />
          <LabelWithText
            label="Prestataire étranger"
            data={<Badge variant="primary" text={valueRender(center.ol, (ol) => getOlName(ol))} />}
          />

          <LabelWithText
            label="Adresse du logement"
            data={
              !centerAddress ? (
                '-'
              ) : (
                <AddressLink
                  address1={centerAddress?.address ?? ''}
                  country={nativeCountryName(centerAddress?.country.name) ?? ''}
                />
              )
            }
          />

          <CenterAddressCoursesLabel data={center} />

          <LabelWithText
            label="Hébergement"
            data={valueRender(center.accomodation, (value) => accomodationEnumMapping[value])}
          />
          <LabelWithText label="Âge min" data={valueRender(center.minAge)} />
          <LabelWithText label="Âge max" data={valueRender(center.maxAge)} />
          <LabelWithText
            label="Classe min"
            data={valueRender(center.minSchoolLevel, (level) => schoolLevelEnumMapping[level])}
          />
          <LabelWithText
            label="Classe max"
            data={valueRender(center.maxSchoolLevel, (level) => schoolLevelEnumMapping[level])}
          />
        </div>
      </InnerBox>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Informations '}
          icon={TestIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <CenterForm
            className={MODAL_CONTENT_CLASSNAME}
            mode="global-view"
            defaultValues={CenterProperties.mapCenterToInput(selectedData)}
            restrictedAccomodation={
              centerActions?.canUpdateAccomodation ? undefined : center.accomodation
            }
            buttonsConfig={modalButtonConfig({
              cancelText: 'Fermer',
              onCancel: () => closeModel('edit'),
              isLoading: updateCenterMutation.isPending,
              confirmText: 'Valider',
              onConfirm: async ({ assignJourneyToCenter: _assignJourneyToCenter, ...rest }) => {
                await updateCenterMutation.mutateAsync({
                  id: selectedData.id,
                  ...rest
                })
                closeModel('edit')
              }
            })}
          />
        </Modal>
      )}
    </InnerBox>
  )
}

export default CenterGlobalViewInformationSection
